// React
import React, { useState, useContext, useMemo } from 'react';

// Mertial UI components
import { Divider, Typography } from '@mui/material';

// Custom components
import EmailWithEdit from 'components/TemplateDashboard/TemplateSettingsModal/RightPanels/EmailTriggers/EmailWithEdit';
import EmailInput from './EmailInput';

// Icons
import AddIcon from 'components/Icons/addIcon';

// Styled
import { Wrapper, AddEmailButton } from './styled';

import {
  RightPanel,
  RightHeader,
  RightHeaderTextBox,
  RightHeaderTextBold,
  RightHeaderTextNormal,
  PanelContentWrapper,
  HeadingText,
} from '../commonPanelStyled';

// Context
import { SettingsContext } from 'components/TemplateDashboard/TemplateSettingsModal/context';

// Hooks
import useEmailTriggers from 'components/TemplateDashboard/TemplateSettingsModal/hooks/useEmailTriggers';
import UsersInput from './UsersInput/UsersInput';
import EmailConfigurations from 'components/EmailConfigurations';
import { TemplateType } from 'pages/TemplateDashboard/context';
import usePatchTemplate from '../../hooks/usePatchTemplate';
import { useSelector } from 'react-redux';
import selectors from 'store/selectors';
import { getRoles } from 'store/selectors/roles';

interface PropTypes {
  template: TemplateType;
  updateTemplateCb: (template: TemplateType) => void;
}

const EmailTriggers = ({ template, updateTemplateCb }: PropTypes) => {
  const ctx = useContext(SettingsContext);

  const [addEmail, setAddEmail] = useState(false);
  const [addEmailEmptyState, setAddEmailEmptyState] = useState(false);

  const usersHash = useSelector(selectors.getHashedUserList);
  const teamsHash = useSelector(selectors.getTeamsHash);
  const locationsHash = useSelector(selectors.getLocationsHash);
  const roles = useSelector(getRoles);
  const { list: rolesList } = roles;

  const { loading, addEmailTrigger, editEmailTrigger, deleteEmailTrigger } =
    useEmailTriggers();

  const { updateEmailConfiguration } = usePatchTemplate({
    updateTemplateCb,
  });

  const handleAddEmail = async (email: string) => {
    const added = await addEmailTrigger({ email: email, type: 'EMAIL' });
    if (added) {
      setAddEmailEmptyState(false);
      setAddEmail(false);
    }
  };

  const blacklistIds = useMemo(() => {
    // Check if any submission access has isEveryone: true
    if (ctx?.template?.SubmissionAccess) {
      const hasEveryoneAccess = ctx.template.SubmissionAccess.some(
        (access: any) => access.isEveryone === true,
      );
      if (hasEveryoneAccess) {
        return [];
      }
    }

    // Get all accessor IDs from template submission access
    const submissionAccessIds = new Set<string>();

    if (ctx?.template?.SubmissionAccess) {
      ctx.template.SubmissionAccess.forEach((access: any) => {
        if (access.AccessorId) {
          submissionAccessIds.add(access.AccessorId);
        }
      });
    }

    // Create blacklist of all IDs that are NOT in submission access
    const blacklist = new Set<string>();

    // Add all user IDs that are not in submission access
    Object.keys(usersHash || {}).forEach((userId) => {
      if (!submissionAccessIds.has(userId)) {
        blacklist.add(userId);
      }
    });

    // Add all team IDs that are not in submission access
    Object.keys(teamsHash || {}).forEach((teamId) => {
      if (!submissionAccessIds.has(teamId)) {
        blacklist.add(teamId);
      }
    });

    // Add all location IDs that are not in submission access
    Object.keys(locationsHash || {}).forEach((locationId) => {
      if (!submissionAccessIds.has(locationId)) {
        blacklist.add(locationId);
      }
    });

    // Add all role IDs that are not in submission access
    (rolesList || []).forEach((role) => {
      if (role.id && !submissionAccessIds.has(role.id)) {
        blacklist.add(role.id);
      }
    });

    return Array.from(blacklist);
  }, [ctx?.template, usersHash, teamsHash, locationsHash, rolesList]);

  return (
    <RightPanel>
      <RightHeader>
        <RightHeaderTextBox>
          <RightHeaderTextBold>Notifications (Email)</RightHeaderTextBold>
          <RightHeaderTextNormal>
            Enter email addresses to receive a notification when this template
            is submitted.
          </RightHeaderTextNormal>
        </RightHeaderTextBox>
      </RightHeader>
      <PanelContentWrapper>
        <HeadingText>Recipients</HeadingText>
        <Typography
          fontWeight={500}
          fontSize={'13px'}
          lineHeight={'19.12px'}
          marginBottom="10px"
        >
          Submission email notifications will be triggered based on the
          submission access
        </Typography>
        <UsersInput
          defaultUsers={ctx?.emailTriggers}
          showSelectedLimit={2}
          blacklistIds={[]}
        />
        <Divider
          sx={{
            margin: '10px 0',
            BorderColor: '#E0E0E0',
          }}
        />
        <HeadingText>Additional Email Addresses</HeadingText>
        {addEmailEmptyState && (
          <EmailInput
            onClickAdd={handleAddEmail}
            onClickCancel={() => setAddEmailEmptyState(false)}
          />
        )}
        {ctx?.emailTriggers?.length ? (
          <React.Fragment>
            <Wrapper>
              {ctx.emailTriggers.map(
                (trigger, idx) =>
                  trigger.type === 'EMAIL' && (
                    <EmailWithEdit
                      editEmailTrigger={editEmailTrigger}
                      deleteEmailTrigger={deleteEmailTrigger}
                      data={trigger}
                      key={trigger.id}
                    />
                  ),
              )}
            </Wrapper>
            {!addEmail && (
              <AddEmailButton
                onClick={() => setAddEmail(true)}
                variant="text"
                startIcon={<AddIcon />}
              >
                Add Email Recipient
              </AddEmailButton>
            )}
            {addEmail && (
              <EmailInput
                onClickAdd={(email) => handleAddEmail(email)}
                onClickCancel={() => setAddEmail(false)}
              />
            )}
          </React.Fragment>
        ) : !addEmailEmptyState && !loading ? (
          <>
            {!addEmail && (
              <AddEmailButton
                onClick={() => setAddEmail(true)}
                variant="text"
                startIcon={<AddIcon />}
              >
                Add Email Recipient
              </AddEmailButton>
            )}
            {addEmail && (
              <EmailInput
                onClickAdd={(email) => handleAddEmail(email)}
                onClickCancel={() => setAddEmail(false)}
              />
            )}
          </>
        ) : (
          <></>
        )}

        <Divider
          sx={{
            margin: '10px 0',
            BorderColor: '#E0E0E0',
          }}
        />
        <EmailConfigurations
          selected={template?.isSingleEmailThread}
          onChangeCB={(selected) => updateEmailConfiguration(selected)}
        />
      </PanelContentWrapper>
    </RightPanel>
  );
};

export default EmailTriggers;
